import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ButtonGhostV2,
  ButtonGhostCompact,
  colors2,
  Divider,
  makeArrayFromLength,
  Modal,
  OKELink,
  SkeletonLoader,
  SwitchSingle,
  UI_BODY_SEMIBOLD_SM,
  UI_BODY_SM,
  UI_BODY_SM_DF,
  UI_CAPTION,
  UI_TAG,
} from "oolib";
import { DropdownSingle } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import { TabBarStyle2 } from "oolib/dist/v2/components/TabBar";
import styled from "styled-components";
import { CaretDown } from "@phosphor-icons/react";
import { FILTER_IDS } from "@okf/ootils";

import TableElem from "@/components/cards/TableElem";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import {
  useGetTypeCategoryAndTags,
  convertTagDocsDataToOptions,
} from "@/components/inputs/DynamicTagsInputs/utils";
import {
  getDisplayText,
  getParentData,
} from "@/components/inputs/DynamicTagsInputs/FilterInput/utils/annoHierarchyHelpers";
import { handleOnChange as handleFilterChange } from "@/components/discovery/FilterModule/utils/handleOnChange";

import {
  PrevalenceSections,
  useTopicPrevalence,
  buildSubThemeColorIdxMap,
  groupAnnotationDataBySubTheme,
} from "@/components/analysisModules/comps/AnnotationPrevalence";
import { flattenFilterConfig } from "@/components/discovery/FilterModule/utils/flattenFilterConfig";
import { useTagCooccurrence } from "./queries/useTagCooccurrence";
import TooltipV2 from "@/components/UI/Tooltip";
import { MAX_COLOR_CODE_COLORS } from "../../utils/translateColorCodeFiltersToBE";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import { getTenant } from "@/TENANT_CONFIGS";

// Wrapper passed to TableElem via its `TableWrapper` prop — adds horizontal
// padding so the cooccurrence table sits inset from the panel edges. Keeps
// the padding scoped to this table (other panel sections are untouched).
const TablePaddedWrapper = ({ children }) => (
  <div style={{ padding: "0 1rem" }}>{children}</div>
);

// Custom select trigger used by toolbar dropdowns (pair-scope, view).
// Underlined caret + value text, no chunky form styling — same look as the
// (now-removed) per-subTheme picker we had earlier in prevalence.
const StyledToolbarSelectTrigger = styled.button`
  background: none;
  border: 1px solid ${colors2.grey20};
  border-radius: 4px;
  padding: 2.5px 5px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover {
    background: ${colors2.grey5};
  }
`;

// Plain-language explanation of the cooccurrence metrics — opens from the
// "How to read this?" link in the toolbar. Kept as a static component since
// the content doesn't depend on the current view.
const HOW_TO_READ_ROWS = [
  {
    metric: "Count",
    answers: "How many annotations mention both?",
    use: "Raw volume — what's loud",
  },
  {
    metric: "Strength",
    answers: "Above background popularity?",
    use: "Finding genuine relationships",
  },
  {
    metric: "Overlap",
    answers: "Do these tags share the same annotations?",
    use: "Spotting near-duplicates / synonyms",
  },
  {
    metric: "Predictability",
    answers: "If one appears, how often does the other follow?",
    use: "Finding implication patterns",
  },
];

const HOW_TO_READ_RULES = [
  "High Count + High Strength → headline insight (real and frequent)",
  "High Count + ~1 Strength → popular tags that just co-occur; low signal",
  "Low Count + High Strength → rare but meaningful; worth investigating",
  "High Overlap → the tags are practically interchangeable (duplicate candidates)",
];

const HowToReadModal = ({ onClose }) => (
  <Modal
    title="Reading the co-occurrence table"
    onClose={onClose}
    desktopWidth="640px"
    fitToContentHeight
  >
    <div
      style={{
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        gap: "1.6rem",
      }}
    >
      <UI_BODY_SM>
        Each row shows a pair of tags that appear together in your annotations.
        Use these metrics to understand what's actually interesting:
      </UI_BODY_SM>

      <div
        style={{
          border: `1px solid ${colors2.grey10}`,
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: "flex",
            padding: "0.8rem 1rem",
            background: colors2.grey3,
            borderBottom: `1px solid ${colors2.grey10}`,
            gap: "1rem",
          }}
        >
          <div style={{ flex: 1 }}>
            <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey60 }}>Metric</UI_BODY_SEMIBOLD_SM>
          </div>
          <div style={{ flex: 2 }}>
            <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey60 }}>What it answers</UI_BODY_SEMIBOLD_SM>
          </div>
          <div style={{ flex: 2 }}>
            <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey60 }}>When to use</UI_BODY_SEMIBOLD_SM>
          </div>
        </div>
        {/* Data rows */}
        {HOW_TO_READ_ROWS.map((row, idx) => (
          <div
            key={row.metric}
            style={{
              display: "flex",
              padding: "0.8rem 1rem",
              borderTop: idx > 0 ? `1px solid ${colors2.grey10}` : "none",
              gap: "1rem",
            }}
          >
            <div style={{ flex: 1 }}>
              <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{row.metric}</UI_BODY_SEMIBOLD_SM>
            </div>
            <div style={{ flex: 2 }}>
              <UI_BODY_SM style={{ color: colors2.grey80 }}>{row.answers}</UI_BODY_SM>
            </div>
            <div style={{ flex: 2 }}>
              <UI_BODY_SM style={{ color: colors2.grey80 }}>{row.use}</UI_BODY_SM>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <UI_BODY_SEMIBOLD_SM>Quick rules of thumb</UI_BODY_SEMIBOLD_SM>
        {HOW_TO_READ_RULES.map((rule) => (
          <UI_BODY_SM key={rule} style={{ color: colors2.grey80 }}>
            · {rule}
          </UI_BODY_SM>
        ))}
      </div>
    </div>
  </Modal>
);

// Horizontal divider that the user can drag to resize the split halves.
// Visual style mirrors SidebarLayoutV2's resize-edge convention: 1px line
// in grey10 by default, 2px in colors2.blue on hover/active. Hit area is
// 8px tall (the visible line is centered) so the divider is easy to grab.
const SplitResizeHandle = ({ topPct, isResizing, onStart }) => {
  const [hovering, setHovering] = useState(false);
  const active = isResizing || hovering;
  return (
    <div
      onMouseDown={onStart}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        position: "absolute",
        top: `${topPct}%`,
        left: 0,
        right: 0,
        height: "8px",
        transform: "translateY(-50%)",
        cursor: "ns-resize",
        background: "transparent",
        zIndex: 2,
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          height: active ? "2px" : "1px",
          background: active ? colors2.blue : colors2.grey10,
          transition: "height 0.1s, background 0.1s",
        }}
      />
    </div>
  );
};

const PAIR_SCOPE_OPTIONS = [
  { display: "Thematic × Attribute", value: "cross" },
  { display: "Thematic × Thematic", value: "thematic" },
  { display: "Attribute × Attribute", value: "attribute" },
  { display: "All", value: "all" },
];

// View options. BE owns each view's filter recipe (so it can apply absolute
// thresholds derived from totalAnnotations). The FE owns each view's default
// sort (the BE accepts whatever we send). Captions render above the table.
const VIEW_OPTIONS = [
  {
    value: "topTopicPairings",
    display: "Top topic pairings",
    defaultSort: { count: -1 },
    caption:
      "Thematic tag pairs ranked by volume, filtered to real associations (strength ≥ 1.2).",
  },
  {
    value: "negativePulse",
    display: "Negative topic pulse",
    defaultSort: { count: -1 },
    caption:
      "Topics with the most negative-tagged mentions, filtered to genuine associations (strength ≥ 1.2 — removes pairs that co-occur only because both tags are individually common).",
  },
  {
    value: "positivePulse",
    display: "Positive topic pulse",
    defaultSort: { count: -1 },
    caption:
      "Topics with the most positive-tagged mentions, filtered to genuine associations (strength ≥ 1.2 — removes pairs that co-occur only because both tags are individually common).",
  },
  {
    value: "signalsByRole",
    display: "Signal roles",
    defaultSort: { count: -1 },
    caption:
      "Which signals tend to play which roles. Volume-ranked, filtered to real associations (strength ≥ 1.2).",
  },
  {
    value: "signalsByReliability",
    display: "Signal reliability",
    defaultSort: { count: -1 },
    caption:
      "Which signals tend to be more or less reliable. Volume-ranked, filtered to real associations (strength ≥ 1.2).",
  },
  {
    value: "signalsByFeatures",
    display: "Signals by feature",
    defaultSort: { count: -1 },
    caption:
      "Which product/service features each signal touches. Volume-ranked, filtered to real associations (strength ≥ 1.2). Only on tenants with a Features subTheme.",
  },
  {
    value: "mostDiscussed",
    display: "Raw cooccurrence",
    defaultSort: { count: -1 },
    caption: null,
  },
];

const MODE_TAB_OPTIONS = [
  { value: "prevalence", display: "Prevalence" },
  { value: "cooccurrence", display: "Co-occurrence" },
  { value: "split", display: "Split" },
];

const TAXONOMY_TAB_OPTIONS = [
  { value: "thematic", display: "Thematics" },
  { value: "attribute", display: "Attributes" },
];

// The colour-code control in a prevalence section's header. Handed to
// PrevalenceSections as its section action; the Annotation Counts widget
// renders the same sections without one.
const PaintBucketAction = ({ group, onToggleBreakdown, isBreakdownActive }) => {
  if (!onToggleBreakdown) return null;
  const active = !!isBreakdownActive?.(group.subThemeId);
  // Number of tags on this subTheme that have data in current scope — same
  // denominator the prevalence bars are using. When it exceeds the
  // color-code cap, only the top N (by count) will color-code; tooltip
  // surfaces that.
  const tagCount = (group.tags || []).length;
  const truncated = tagCount > MAX_COLOR_CODE_COLORS;
  const tooltipText = truncated
    ? `Color code will apply to the top ${MAX_COLOR_CODE_COLORS} tags by count (of ${tagCount}).`
    : null;
  const button = (
    <ButtonGhostCompact
      icon="PaintBucket"
      iconWeight={active ? "fill" : "bold"}
      iconColor={active ? colors2.purple : undefined}
      onClick={() => onToggleBreakdown(group.subThemeId)}
    />
  );
  return tooltipText ? (
    <TooltipV2 text={tooltipText} placement="bottom">{button}</TooltipV2>
  ) : button;
};

// Cooccurrence table — single-column "Pair" + count-bar column. The
// programmatic tableConfig is built fresh on each render so the count-bar's
// maxCount tracks the current top pair.
const CooccurrenceSection = ({ queryActiveFilters, enabled, onRowClick, stickyTop = "0", subThemeDocs = [] }) => {
  // "mostDiscussed" → raw top-N (user-controlled toolbar)
  // "topTopicPairings" → BE locks pairScope=thematic; default view
  // "negativePulse" / "positivePulse" → BE anchors at the Negative/Positive
  //   Sentiment Polarity tag; FE collapses pair display to the topic side.
  const [view, setView] = useState("topTopicPairings");
  // Debug switch for opinionated views — when ON, exposes the extra
  // diagnostic columns (count/strength/overlap/predictability that aren't
  // the primary ranking axis for that view).
  const [debugCols, setDebugCols] = useState(false);
  // Debug switch — when ON, BE zeros out the view's noise filters (count
  // floor, strength floor, overlap cap) so the user can see what was being
  // filtered out. Affects which rows the query returns, not just display.
  const [debugNoFilters, setDebugNoFilters] = useState(false);
  // "cross" (default) → Thematic × Attribute
  // "thematic"        → Thematic × Thematic only
  // "attribute"       → Attribute × Attribute only
  // "all"             → no role filter
  // "tva:<attrSubThemeId>" → Thematic × (one specific attribute subTheme).
  //                          Translated at send-time into an explicit
  //                          `sides` payload (IDs only) since the BE's
  //                          pairScope shortcut can't express it.
  const [pairScope, setPairScope] = useState("cross");

  // Split subThemeDocs by taxonomyRole. Attribute subThemes feed the
  // dynamic "Thematic × <attr>" options below; thematic subTheme ids feed
  // the sideA payload when one of those options is picked.
  const { attributeSubThemes, thematicSubThemeIds } = useMemo(() => {
    const attrs = [];
    const themeIds = [];
    for (const st of subThemeDocs) {
      const role = st.meta?.taxonomyRole === "attribute" ? "attribute" : "thematic";
      if (role === "attribute") {
        attrs.push({
          _id: st._id,
          display: st.display || st.main?.title || st._id,
        });
      } else {
        themeIds.push(st._id);
      }
    }
    return { attributeSubThemes: attrs, thematicSubThemeIds: themeIds };
  }, [subThemeDocs]);

  // Dropdown options = static four (cross/thematic/attribute/all) + a
  // per-attribute-subTheme "Thematic × <attr name>" row.
  const pairScopeOptions = useMemo(
    () => [
      ...PAIR_SCOPE_OPTIONS,
      ...attributeSubThemes.map((st) => ({
        value: `tva:${st._id}`,
        display: `Thematic × ${st.display}`,
      })),
    ],
    [attributeSubThemes]
  );

  // Translate the dropdown selection into the API params: if the user picked
  // a "tva:<id>" option, send explicit sides (IDs only) and clear pairScope.
  const { apiPairScope, apiSides } = useMemo(() => {
    if (typeof pairScope === "string" && pairScope.startsWith("tva:")) {
      const attrId = pairScope.slice(4);
      return {
        apiPairScope: null,
        apiSides: {
          sideA: { subThemeIds: thematicSubThemeIds },
          sideB: { subThemeIds: [attrId] },
        },
      };
    }
    return { apiPairScope: pairScope, apiSides: undefined };
  }, [pairScope, thematicSubThemeIds]);
  // Default true → skip pairs where both tags share a subTheme (e.g. two
  // sentiment-polarity tags together). Toggle off to also surface
  // within-subTheme pairs.
  const [excludeSameSubTheme, setExcludeSameSubTheme] = useState(true);
  const [howToReadOpen, setHowToReadOpen] = useState(false);
  // TableElem's backend-sort state. Shape: `{ sort: { [path]: 1 | -1 } }`,
  // passed straight through to useTagCooccurrence which forwards it as the
  // BE's `sort` param. Sort needs to happen BE-side because we slice to
  // top-N — sorting an already-sliced top-20-by-count locally would lie
  // about which rows are top-20-by-strength. Initial sort matches the
  // default view's preferred sort.
  const [backendActiveSort, setBackendActiveSort] = useState({
    sort: { strength: -1 },
  });

  // When the user switches view, also swap the sort to the view's preferred
  // default. They can still override by clicking a column header — this
  // only sets the starting point.
  const handleViewChange = (nextView) => {
    setView(nextView);
    const next = VIEW_OPTIONS.find((v) => v.value === nextView);
    if (next?.defaultSort) {
      setBackendActiveSort({ sort: next.defaultSort });
    }
  };

  const { data, status, error } = useTagCooccurrence({
    activeFilters: queryActiveFilters,
    pairScope: apiPairScope,
    sides: apiSides,
    excludeSameSubTheme,
    sort: backendActiveSort.sort,
    view,
    removeNoiseFilters: debugNoFilters,
    enabled,
  });

  const viewOption = VIEW_OPTIONS.find((v) => v.value === view) || VIEW_OPTIONS[0];
  const pairScopeDisplay =
    pairScopeOptions.find((o) => o.value === pairScope)?.display || "";

  const rows = data?.data || [];
  const maxCount = rows[0]?.count || 0;

  const tableConfig = useMemo(() => {
    // Views with an opinionated, preset sort hide the column sort UI so users
    // can't change it. The user gets the view's intended ranking and nothing
    // else. "Most discussed" stays sortable.
    const isSortLocked =
      view === "topTopicPairings" ||
      view === "negativePulse" ||
      view === "positivePulse" ||
      view === "signalsByRole" ||
      view === "signalsByReliability" ||
      view === "signalsByFeatures";
    const sortable = (spec) => (isSortLocked ? {} : { backendSortConfig: spec });
    // Anchored views show only one side of the pair (the variable side);
    // the anchor (e.g. "Negative" for negativePulse) is implicit in the
    // view name and would be redundant on every row.
    const isAnchoredView = view === "negativePulse" || view === "positivePulse";

    // Column definitions keyed by colId. The actual cellData array is built
    // by `columnOrderByView` so per-view orderings can lead with the column
    // each view is sorted by (e.g. Strength first for Topic Companions,
    // Count first for Negative/Positive Pulse).
    const COLUMNS_BY_ID = {
      pair: {
        colId: "pair",
        comp: "TablePairDisplay",
        headerDisplay: isAnchoredView ? "Topic" : "Pair",
        // valuePath must point to something truthy on the row so TableCell's
        // populated-check doesn't fall back to a "-". TablePairDisplay reads
        // tagA/tagB from tableRowData directly and ignores `value`.
        valuePath: "tagA",
        props: { hideTagB: isAnchoredView },
      },
      count: {
        colId: "count",
        comp: "TableCountBar",
        headerDisplay: "Count",
        valuePath: "count",
        // colorIdx 5 = dataVizColorsLight[5] (light blue) — matches the
        // pale-blue bar tone from the original analytics-table reference.
        props: { maxCount, colorIdx: 5 },
        ...sortable({ sort: { count: 1 }, defaultSort: true }),
        infoTooltip: {
          text:
            "Number of annotations in which both tags appear together. Raw volume — useful but doesn't tell you whether the pairing is meaningful or just because both tags are popular.",
          placement: "bottom",
        },
      },
      strength: {
        colId: "strength",
        comp: "TableStrengthRamp",
        headerDisplay: "Strength",
        valuePath: "strength",
        ...sortable({ sort: { strength: 1 } }),
        infoTooltip: {
          text:
            "How much MORE often this pair appears together than chance would predict. Above 1 = real relationship. Around 1 = just noise (the tags are together because both are popular, not because they're related). Below 1 = they tend to avoid each other.",
          placement: "bottom",
        },
      },
      overlap: {
        colId: "overlap",
        comp: "TablePercent",
        headerDisplay: "Overlap",
        valuePath: "overlap",
        ...sortable({ sort: { overlap: 1 } }),
        infoTooltip: {
          text:
            "Of annotations that mention either tag, the fraction that mention both. Closer to 100% means the tags share almost the same annotations (possible duplicates, synonyms, tightly coupled concepts). Pair with Strength to tell 'real relationship' apart from 'both popular'.",
          placement: "bottom",
        },
      },
      predictability: {
        colId: "predictability",
        comp: "TablePercent",
        headerDisplay: "Predictability",
        valuePath: "predictability",
        ...sortable({ sort: { predictability: 1 } }),
        infoTooltip: {
          text:
            "Read the arrow as 'implies'. `A → B at 75%` means: when A appears, B also appears 75% of the time. The arrow always points in the stronger direction — the reverse (B → A) is weaker and not shown.",
          placement: "bottom",
        },
      },
    };

    // Per-view column order. Lead with the column the view sorts by so the
    // ranking axis is the first thing the eye lands on. All current views
    // sort by count desc, so the default order leads with count.
    const columnOrder = ["pair", "count", "strength", "overlap", "predictability"];

    // Opinionated views (everything except mostDiscussed) hide diagnostic
    // columns (3-5) by default to keep the product surface clean.
    // `debugCols` toggles them back on.
    const hasDebugToggle = view !== "mostDiscussed";
    const visibleOrder =
      hasDebugToggle && !debugCols ? columnOrder.slice(0, 2) : columnOrder;

    const cellData = visibleOrder.map((id) => COLUMNS_BY_ID[id]);
    const colWidths = visibleOrder.reduce((acc, id) => {
      acc[id] = id === "pair" ? 180 : 150;
      return acc;
    }, {});

    return {
      // No `contentType` here — keeps useParseTplToTableConfig in pass-through
      // mode (no TPL fetch). `rowIdPath` is required by TableElem to derive
      // each row's id (used for selection state, virtualization keys).
      // `rowHeight`: sized for the worst-case vertical pair display —
      // 2 lines tag + 1 line subTheme (×2) + caret. Tag titles clamp at 2
      // lines so the row height is predictable. Anchored views show only
      // one tag block, so the row collapses to ~half the height.
      rowIdPath: "_id",
      rowHeight: isAnchoredView ? 56 : 124,
      colWidths,
      cellData,
    };
  }, [maxCount, view, debugCols]);

  const tableData = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        // TableElem needs a unique row id; compose from tagIds.
        _id: `${r.tagA?.tagId}__${r.tagB?.tagId}`,
      })),
    [rows]
  );

  // Build the body section conditionally. We render the toolbar
  // (View dropdown + optional row-2 controls) ALWAYS so the user can
  // switch views even when the current view returns nothing.
  const isLoading = status === "loading";
  const isError = status === "error";
  const isEmpty = !isLoading && !isError && !rows.length;

  // Per-view empty-state copy. Each tells the user (a) WHY this view is
  // empty (which filter is responsible) and (b) what to try next.
  const emptyMessageByView = {
    mostDiscussed:
      "No tag pairs found in this selection. Try widening your filters or toggling Cross-category.",
    topTopicPairings:
      "No thematic pairs meet the count + strength filters. Try switching to Raw cooccurrence.",
    negativePulse:
      "No topics with Negative-tagged annotations meet the filters here. Try switching to Raw cooccurrence to see what's there.",
    positivePulse:
      "No topics with Positive-tagged annotations meet the filters here. Try switching to Raw cooccurrence to see what's there.",
    signalsByRole:
      "No Signal × Role pairs meet the filters. Either this tenant doesn't have a Signals + Signal Role subTheme, or no pairs cleared the count/strength floors.",
    signalsByReliability:
      "No Signal × Reliability pairs meet the filters. Either this tenant doesn't have a Signals + Signal Reliability subTheme, or no pairs cleared the count/strength floors.",
    signalsByFeatures:
      "No Signal × Feature pairs meet the filters. Either this tenant doesn't have a Features subTheme, or no pairs cleared the count/strength floors.",
  };
  const emptyMessage =
    emptyMessageByView[view] ||
    "No pairs match this view's filters. Try Raw cooccurrence.";

  return (
    <div>
      {/* Row 1: View dropdown only — STICKY with border-bottom so the
          primary "what am I looking at" control stays visible while
          scrolling. Debug + pair-scope rows below are NOT sticky. */}
      <div
        style={{
          position: "sticky",
          top: stickyTop,
          zIndex: 4,
          background: "white",
          borderBottom: `1px solid ${colors2.grey10}`,
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          padding: "0.6rem 1rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <UI_TAG style={{ color: colors2.grey60 }}>View:</UI_TAG>
          <DropdownSingle
            id="cooccurrenceView"
            value={view}
            onChange={(_k, v) => handleViewChange(v)}
            options={VIEW_OPTIONS}
            saveValueAsString
            popOutOfOverflowHiddenParent
            genCustomSelectComp={() => (
              <StyledToolbarSelectTrigger>
                <UI_TAG style={{ color: colors2.grey80 }}>
                  {viewOption.display}
                </UI_TAG>
                <CaretDown size={10} weight="bold" color={colors2.grey60} />
              </StyledToolbarSelectTrigger>
            )}
          />
        </div>
      </div>
      {/* Debug row — only for opinionated views. NOT sticky (scrolls with
          the table). "Show columns" exposes columns 3-5; "Remove filters"
          asks the BE to skip the view's noise filters (count floor,
          strength floor, overlap cap). */}
      {view !== "mostDiscussed" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            padding: "0.6rem 1rem",
          }}
        >
          <UI_TAG style={{ color: colors2.grey60 }}>Debug:</UI_TAG>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <UI_TAG style={{ color: colors2.grey80 }}>Show columns</UI_TAG>
            <SwitchSingle
              id="cooccurrenceDebugCols"
              option={{ display: "", value: true }}
              value={debugCols ? true : undefined}
              onChange={(_k, v) => setDebugCols(!!v)}
              saveValueAsString
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <UI_TAG style={{ color: colors2.grey80 }}>Remove filters</UI_TAG>
            <SwitchSingle
              id="cooccurrenceDebugNoFilters"
              option={{ display: "", value: true }}
              value={debugNoFilters ? true : undefined}
              onChange={(_k, v) => setDebugNoFilters(!!v)}
              saveValueAsString
            />
          </div>
        </div>
      )}
      {/* Row 2: pair-scope + cross-category + help link. Not sticky; scrolls
          away with the rest of the content. Entire row is hidden in views
          that lock these settings (everything except mostDiscussed). */}
      {view === "mostDiscussed" && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            padding: "0.6rem 1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "8px",
              flex: "none",
            }}
          >
            <DropdownSingle
              id="cooccurrencePairScope"
              value={pairScope}
              onChange={(_k, v) => setPairScope(v)}
              options={pairScopeOptions}
              saveValueAsString
              popOutOfOverflowHiddenParent
              genCustomSelectComp={() => (
                <StyledToolbarSelectTrigger>
                  <UI_TAG style={{ color: colors2.grey80 }}>
                    {pairScopeDisplay}
                  </UI_TAG>
                  <CaretDown size={10} weight="bold" color={colors2.grey60} />
                </StyledToolbarSelectTrigger>
              )}
            />
            {/* Sibling UI_TAG label (vs. SwitchSingle's built-in display) so the
                typography matches the dropdown's custom-select trigger. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                flex: "none",
              }}
            >
              <UI_TAG style={{ color: colors2.grey80 }}>Cross-category</UI_TAG>
              <SwitchSingle
                id="cooccurrenceExcludeSameSubTheme"
                option={{ display: "", value: true }}
                value={excludeSameSubTheme ? true : undefined}
                onChange={(_k, v) => setExcludeSameSubTheme(!!v)}
                saveValueAsString
              />
            </div>
          </div>
          <div style={{ flex: "none" }}>
            {/* color="black" — sets BOTH text and underline to black via
                OKELink's genColor (style prop can't override the ::after
                underline color). UI_TAG inherits via color: inherit so the
                small typography is preserved. */}
            <OKELink color="black" onClick={() => setHowToReadOpen(true)}>
              <UI_TAG style={{ color: "inherit" }}>How to read this?</UI_TAG>
            </OKELink>
          </div>
        </div>
      )}
      {howToReadOpen && <HowToReadModal onClose={() => setHowToReadOpen(false)} />}
      {viewOption.caption && (
        <div style={{ padding: "0 1rem 0.4rem" }}>
          <UI_CAPTION style={{ color: colors2.grey60 }}>{viewOption.caption}</UI_CAPTION>
        </div>
      )}
      {isLoading && (
        <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {makeArrayFromLength(8).map((_, i) => (
            <SkeletonLoader key={i} style={{ width: "100%", height: "2.8rem" }} />
          ))}
        </div>
      )}
      {isError && (
        <div style={{ padding: "1rem" }}>
          <UI_BODY_SM style={{ color: colors2.red60 }}>
            Could not load co-occurrence data.
            {error?.message ? ` ${error.message}` : ""}
          </UI_BODY_SM>
        </div>
      )}
      {isEmpty && (
        <div style={{ padding: "1rem" }}>
          <UI_BODY_SM style={{ color: colors2.grey60 }}>
            {emptyMessage}
          </UI_BODY_SM>
        </div>
      )}
      {!isLoading && !isError && !isEmpty && (
        <TableElem
          id="annoExplorerVizPanelCooccurrence"
          status="success"
          data={tableData}
          tableConfig={tableConfig}
          TableWrapper={TablePaddedWrapper}
          handleRowClick={onRowClick}
          disableRowStripes={false}
          // lazyLoad.backendActiveSort puts TableElem's header into backend-
          // sort mode. The other lazyLoad fields are intentionally omitted
          // (we don't paginate this table — top-N is the entire data set).
          lazyLoad={{
            backendActiveSort,
            setBackendActiveSort,
          }}
        />
      )}
      {/* Bottom breathing room matching the toolbar's top padding. */}
      <div style={{ height: "0.6rem" }} />
    </div>
  );
};

export const AnnotationExplorerVizPanel = ({
  // Full active-filter set used to scope the bar-chart data query (includes
  // the explorer's contentType filter from selected TPLs, etc.).
  queryActiveFilters,
  // The user-editable filter array + setter. Bar-click toggles write here,
  // mirroring what FilterModule writes when the user picks tags manually.
  activeFilters,
  setActiveFilters,
  filterConfigs,
  // Color-code state (non-legacy anno mode). Lets the Prevalence section
  // expose a per-subTheme "Breakdown" shortcut that pre-fills the colorCode
  // filter with all of that subTheme's tags.
  colorCodeFilters,
  setColorCodeFiltersSinglePick,
  onClose,
}) => {
  // Co-occurrence (and its dependent "split" mode) is gated by
  // FEATURES_DISABLED_ON_PROD. On prod, the tabs are hidden unless the
  // current tenant is whitelisted. Co-occurrence is the gate — Split is
  // a hybrid of prevalence + cooccurrence and only makes sense when
  // cooccurrence is available.
  const platformConfigs = useGetQueryData("platformConfigs") || {};
  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } = platformConfigs;
  // Role partition (Thematics/Attributes "Show:" dropdown in prevalence) is
  // a dual-agent concept — split (like legacy) shows ALL sub-theme groups in
  // one list with no picker.
  const showTaxonomyRoleTabs = getAnnoMode(platformConfigs) === "dual-agent";
  // Split mode: the topic-clustering tree (topics → sub-topics) is the topic
  // side of the taxonomy, so prevalence shows it as sections alongside the
  // sub-theme groups.
  const isSplitMode = getAnnoMode(platformConfigs) === "split";
  const isProd = import.meta.env.VITE_ENV === "prod";
  const tenant = getTenant();
  const isCooccurrenceDisabled = isFeatureDisabledOnProd(
    "annoExplorerCooccurrence",
    { disabledFeatures, isProd, tenant }
  );
  const visibleModeTabs = useMemo(
    () =>
      isCooccurrenceDisabled
        ? MODE_TAB_OPTIONS.filter((o) => o.value === "prevalence")
        : MODE_TAB_OPTIONS,
    [isCooccurrenceDisabled]
  );

  const [activeMode, setActiveMode] = useState("prevalence");
  const [activeTaxonomyTab, setActiveTaxonomyTab] = useState("thematic");

  // Defensive: if the active mode becomes unavailable (e.g. user had it
  // on cooccurrence then the config flipped), snap back to prevalence.
  useEffect(() => {
    if (!visibleModeTabs.some((o) => o.value === activeMode)) {
      setActiveMode("prevalence");
    }
  }, [visibleModeTabs, activeMode]);
  // Resizable split divider — splitTopPct controls the top half's height as a
  // percent of the split container. Clamped to [15, 85] so neither half ever
  // collapses to nothing.
  const [splitTopPct, setSplitTopPct] = useState(50);
  const [splitResizing, setSplitResizing] = useState(false);
  const splitContainerRef = useRef(null);

  useEffect(() => {
    if (!splitResizing) return;
    const handleMove = (e) => {
      const el = splitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (!rect.height) return;
      const pct = ((e.clientY - rect.top) / rect.height) * 100;
      setSplitTopPct(Math.max(15, Math.min(85, pct)));
    };
    const handleUp = () => setSplitResizing(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    // Block text selection while dragging — without this, the user's mouse
    // path selects content in the panel and the cursor flicker is annoying.
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [splitResizing]);

  const isPrevalence = activeMode === "prevalence";
  // Split mode renders both sections together — enable the data fetches for
  // each underlying view when either its own mode OR split is active.
  const showPrevalence = activeMode === "prevalence" || activeMode === "split";
  const showCooccurrence = activeMode === "cooccurrence" || activeMode === "split";

  // Per-tag counts. Each row's count is already annotation-unit — the BE
  // groups the annotations collection by tag, so one annotation contributes 1
  // to each distinct tag it carries.
  const {
    data: annotationData,
    status: annotationStatus,
    error: annotationError,
  } = useAggrMetricsAcrossDimensions({
    vizCardId: "annoExplorerVizPanel",
    contentType: "annotations",
    dimensions: [{ valuePath: "annotations.tags.tags", isAnno: true }],
    activeFilters: queryActiveFilters,
    queryOptions: { enabled: showPrevalence },
  });

  // Per-subTheme counts, as a SEPARATE aggregation rather than a sum of the
  // tag counts above: rolling each annotation's tags up to their subThemes
  // de-dupes them, so an annotation carrying three tags from one subTheme
  // counts once for that subTheme instead of three times.
  // includePartialDimensions keeps annotations whose tags have no subTheme —
  // they come back under the "(N/A)" bucket, which maps to "Ungrouped".
  //
  // Both queries return rows carrying a BE-stamped `percentage`: the share of
  // ALL annotations in scope (the BE counts that scope via the same ES count
  // that feeds the listing's "N Annotations" header, so the two can't
  // disagree). The FE computes no percentages of its own.
  const {
    data: subThemeAnnotationCounts,
    status: subThemeCountsStatus,
    error: subThemeCountsError,
  } = useAggrMetricsAcrossDimensions({
    vizCardId: "annoExplorerVizPanelSubThemeCounts",
    contentType: "annotations",
    dimensions: [{ rollupType: "tagType", isAnno: true, path: ["tags", "subThemes"] }],
    activeFilters: queryActiveFilters,
    includePartialDimensions: true,
    queryOptions: { enabled: showPrevalence },
  });

  // Split mode only: the topic-clustering sections, one section per topic
  // with its sub-topics as the bars. Same hook the Annotation Counts widget
  // uses, so the two can't drift.
  const {
    topicGroups,
    status: topicPrevalenceStatus,
    error: topicPrevalenceError,
  } = useTopicPrevalence({
    vizCardIdPrefix: "annoExplorerVizPanel",
    activeFilters: queryActiveFilters,
    enabled: showPrevalence && isSplitMode,
  });

  const tagsQuery = useGetTypeCategoryAndTags({
    tagTypeProp: "tags",
    enableDataFetch: true,
  });
  const subThemesQuery = useGetTypeCategoryAndTags({
    tagTypeProp: "subThemes",
    enableDataFetch: true,
  });

  const tagDocs = useMemo(
    () =>
      tagsQuery.status === "success" && tagsQuery.data
        ? convertTagDocsDataToOptions(tagsQuery.data, "tags")
        : [],
    [tagsQuery.data, tagsQuery.status]
  );

  const subThemeDocs = useMemo(
    () =>
      subThemesQuery.status === "success" && subThemesQuery.data
        ? convertTagDocsDataToOptions(subThemesQuery.data, "tags")
        : [],
    [subThemesQuery.data, subThemesQuery.status]
  );

  // Tag id → tag doc, used by the bar-click handler to extract parent
  // theme/subTheme context onto each filter value (see getParentData).
  const tagDocMap = useMemo(() => {
    const map = {};
    tagDocs.forEach((t) => {
      map[t._id] = t;
    });
    return map;
  }, [tagDocs]);

  const allGroups = useMemo(
    () =>
      groupAnnotationDataBySubTheme({
        annotationData,
        subThemeAnnotationCounts,
        tagDocs,
        subThemeDocs,
      }),
    [annotationData, subThemeAnnotationCounts, tagDocs, subThemeDocs]
  );

  const thematicGroups = useMemo(
    () => allGroups.filter((g) => g.taxonomyRole === "thematic"),
    [allGroups]
  );
  const attributeGroups = useMemo(
    () => allGroups.filter((g) => g.taxonomyRole === "attribute"),
    [allGroups]
  );

  // Sub-topic node ids (incl. the Unplaced pseudo rows) — a bar click on one
  // of these toggles the Topics filter, not the tags filter.
  const topicBarValueSet = useMemo(() => {
    const set = new Set();
    for (const g of topicGroups) for (const t of g.tags) set.add(String(t.value));
    return set;
  }, [topicGroups]);

  // Sub-theme slots first (stable across filter changes), then one slot per
  // topic in topic-id order, so topic sections keep their colours too.
  const colorIdxMap = useMemo(() => {
    const map = buildSubThemeColorIdxMap(subThemeDocs);
    const offset = Object.keys(map).length;
    [...topicGroups]
      .map((g) => g.subThemeId)
      .sort()
      .forEach((id, i) => {
        map[id] = offset + i;
      });
    return map;
  }, [subThemeDocs, topicGroups]);

  // Map of color-coded tag id → assigned color (the `badgeColor` the single-
  // pick wrapper stamps on each value, same source the chips + BE payload use).
  // When non-empty, the prevalence bars spotlight these tags in their colors
  // and grey out everything else — including tags beyond the color-code cap
  // (they never make it into this map) and bars in non-color-coded subThemes.
  // `undefined` when no color code is active, so bars keep their normal colors.
  const colorCodeColorByTagId = useMemo(() => {
    const map = {};
    for (const f of colorCodeFilters || []) {
      for (const v of f?.values || []) {
        if (v?.value != null && v?.badgeColor) map[String(v.value)] = v.badgeColor;
      }
    }
    return Object.keys(map).length ? map : undefined;
  }, [colorCodeFilters]);

  // Build canonical filter values (with parent theme/subTheme context) for a
  // list of tagIds, then toggle them on the FILTER_IDS.tags active filter.
  //   - N tags all already applied → remove all (toggle off)
  //   - N tags partially or none applied → add the missing ones
  // Same value shape AnnoHierarchyFilter.makeOnChange produces — anno-filter
  // bucketing works as if these were picked manually.
  const applyTagFilters = (tagIds) => {
    const entries = tagIds
      .map((tagId) => {
        const tagDoc = tagDocMap[tagId];
        if (!tagDoc) {
          console.warn("AnnotationExplorerVizPanel: missing tag doc for", tagId);
          return null;
        }
        return {
          value: tagId,
          display: getDisplayText(tagDoc),
          tagId,
          ...getParentData(tagDoc),
        };
      })
      .filter(Boolean);
    if (!entries.length) return;

    const existing = activeFilters.find((f) => f?.filterId === FILTER_IDS.tags);
    const existingValues = existing?.values || [];
    const existingIds = new Set(existingValues.map((v) => String(v.value)));
    const allApplied = entries.every((e) => existingIds.has(String(e.tagId)));

    const nextValues = allApplied
      ? existingValues.filter(
          (v) => !entries.some((e) => String(e.tagId) === String(v.value))
        )
      : [
          ...existingValues,
          ...entries.filter((e) => !existingIds.has(String(e.tagId))),
        ];

    handleFilterChange({
      id: FILTER_IDS.tags,
      value: nextValues,
      filterConfig: filterConfigs,
      activeFilters,
      setActiveFilters,
      usedIn: "Annotation Explorer",
    });
  };

  // Topics filter (split mode): its config rides inside the anno-hierarchy
  // config, so resolve its filterId from the flattened configs. Values are
  // { value: nodeId, display } — the same shape TopicClusterFilter produces —
  // and the BE handler resolves topics, sub-topics and the Unplaced pseudo
  // ids alike. Same toggle semantics as applyTagFilters.
  const applyTopicFilters = (nodeIds) => {
    const topicsConfig = topicsFilterConfig;
    if (!topicsConfig) {
      console.warn("AnnotationExplorerVizPanel: no Topics filter config in scope");
      return;
    }
    const displayById = {};
    for (const g of topicGroups) for (const t of g.tags) displayById[String(t.value)] = t.display;
    const entries = nodeIds.map((id) => ({ value: String(id), display: displayById[String(id)] || String(id) }));

    const existing = activeFilters.find((f) => f?.filterId === topicsConfig.filterId);
    const existingValues = existing?.values || [];
    const existingIds = new Set(existingValues.map((v) => String(v.value)));
    const allApplied = entries.every((e) => existingIds.has(e.value));

    const nextValues = allApplied
      ? existingValues.filter((v) => !entries.some((e) => e.value === String(v.value)))
      : [...existingValues, ...entries.filter((e) => !existingIds.has(e.value))];

    handleFilterChange({
      id: topicsConfig.filterId,
      value: nextValues,
      filterConfig: filterConfigs,
      activeFilters,
      setActiveFilters,
      usedIn: "Annotation Explorer",
    });
  };

  // Bar click → toggle just that tag (or that sub-topic, for topic sections).
  const onBarClick = (barData) => {
    if (!barData?.value) return;
    if (topicBarValueSet.has(String(barData.value))) applyTopicFilters([barData.value]);
    else applyTagFilters([barData.value]);
  };

  // Per-subTheme "Breakdown" button: writes the existing colorCode state with
  // this subTheme's tag ids — equivalent to opening the color-code picker,
  // choosing this subTheme, and ticking the (top N) tags under it. Toggle
  // semantics: click on an already-active subTheme clears the colorCode.
  //
  // Built from `allGroups` (not `tagDocs`) so the per-subTheme tag order
  // matches the prevalence chart's count-desc order. setColorCodeFiltersSinglePick
  // caps at MAX_COLOR_CODE_COLORS, so passing tags in count-desc order means
  // the visible breakdown colors land on the most-prevalent tags.
  const subThemeTagIdsByCountMap = useMemo(() => {
    const map = {};
    for (const g of [...allGroups, ...topicGroups]) {
      map[g.subThemeId] = (g.tags || []).map((t) => t.value);
    }
    return map;
  }, [allGroups, topicGroups]);

  // Topic sections colour-code through the Topics filter, not the tags one.
  const topicGroupIdSet = useMemo(() => new Set(topicGroups.map((g) => g.subThemeId)), [topicGroups]);
  const topicsFilterConfig = useMemo(
    () => flattenFilterConfig(filterConfigs || []).find((c) => c?.source?.filterType === "topicClusteringNodeType"),
    [filterConfigs]
  );

  const subThemeTagCountMap = useMemo(() => {
    const map = {};
    for (const g of allGroups) {
      map[g.subThemeId] = (g.tags || []).length;
    }
    return map;
  }, [allGroups]);

  const isSubThemeBreakdownActive = (subThemeId) => {
    const subThemeTagIds = subThemeTagIdsByCountMap[subThemeId] || [];
    if (!subThemeTagIds.length) return false;
    const breakdownFilterId = topicGroupIdSet.has(subThemeId) ? topicsFilterConfig?.filterId : FILTER_IDS.tags;
    const tagsFilter = (colorCodeFilters || []).find(
      (f) => f?.filterId === breakdownFilterId
    );
    const activeValueIds = new Set((tagsFilter?.values || []).map((v) => String(v.value)));
    // "Active" iff ANY of this subTheme's tags are in the color-code set.
    // Can't use `every` because setColorCodeFiltersSinglePick caps values
    // at MAX_COLOR_CODE_COLORS — if the subTheme has more tags than that
    // cap, the unmatched tail would always defeat the every-check. Each
    // tag belongs to exactly one subTheme, so `some` is unambiguous.
    return (
      activeValueIds.size > 0 &&
      subThemeTagIds.some((id) => activeValueIds.has(String(id)))
    );
  };

  const toggleSubThemeBreakdown = (subThemeId) => {
    if (!setColorCodeFiltersSinglePick) return;
    if (isSubThemeBreakdownActive(subThemeId)) {
      setColorCodeFiltersSinglePick([]);
      return;
    }
    const tagIds = subThemeTagIdsByCountMap[subThemeId] || [];
    if (!tagIds.length) return;
    if (topicGroupIdSet.has(subThemeId)) {
      // A topic section: colour its sub-topics (count-desc, capped by the
      // single-pick wrapper) through the Topics filter.
      if (!topicsFilterConfig) return;
      const group = topicGroups.find((g) => g.subThemeId === subThemeId);
      const values = (group?.tags || []).map((t) => ({ value: String(t.value), display: t.display }));
      handleFilterChange({
        id: topicsFilterConfig.filterId,
        value: values,
        filterConfig: filterConfigs,
        activeFilters: [],
        setActiveFilters: setColorCodeFiltersSinglePick,
        usedIn: "Annotation Explorer colorCode breakdown",
      });
      return;
    }
    const values = tagIds
      .map((tagId) => {
        const tagDoc = tagDocMap[tagId];
        if (!tagDoc) return null;
        return {
          value: tagId,
          display: getDisplayText(tagDoc),
          tagId,
          ...getParentData(tagDoc),
        };
      })
      .filter(Boolean);
    if (!values.length) return;
    // Mirror what handleFilterChange would produce: filterId + target +
    // values, sourced from the flattened tags filter config.
    handleFilterChange({
      id: FILTER_IDS.tags,
      value: values,
      filterConfig: filterConfigs,
      activeFilters: [], // start fresh — colorCode is single-pick semantics
      setActiveFilters: setColorCodeFiltersSinglePick,
      usedIn: "Annotation Explorer colorCode breakdown",
    });
  };

  // Cooccurrence row click → toggle both tags as a pair.
  // Use `_id` (not the slug) since tagDocMap is keyed by tagDoc._id —
  // matches the bar-click convention.
  const onCooccurrencePairClick = (rowData) => {
    const a = rowData?.tagA?._id;
    const b = rowData?.tagB?._id;
    if (!a || !b) return;
    applyTagFilters([a, b]);
  };

  // Two independent loading gates: the taxonomy (Signals) sections come from
  // the two tag queries and render as soon as they are back; the Topics
  // sections (split mode) wait only on their own two queries. Their queries
  // are separate scans of the annotations collection, so one gate would hold
  // the sections that are ready hostage to the slowest of the four.
  const resolveStatus = (parts) => {
    if (parts.some((s) => s === "error")) return "error";
    if (parts.some((s) => s === "loading" || s === "idle")) return "loading";
    return "success";
  };
  const prevalenceResolvedStatus = resolveStatus([
    annotationStatus,
    subThemeCountsStatus,
    tagsQuery.status,
    subThemesQuery.status,
  ]);
  const topicsResolvedStatus = resolveStatus([topicPrevalenceStatus]);

  const activeGroups = !showTaxonomyRoleTabs
    ? allGroups
    : activeTaxonomyTab === "attribute" ? attributeGroups : thematicGroups;

  // Sticky offset for sub-toolbars (prevalence tabbar, cooccurrence toolbar).
  // In non-split modes everything scrolls in the rsbSection's single scroll
  // context — the mode tabs are sticky at top:0, so sub-toolbars need to
  // stick BELOW them at top: 4rem (the mode header height). In split mode
  // each half is its own overflow:auto scroll container, so sub-toolbars
  // inside use that half's top:0.
  const subToolbarStickyTop = activeMode === "split" ? "0" : "4rem";

  return (
    <div>
      {/* Sticky header — mode tabs + close. Subtabs (Thematic/Attribute)
          live inside the Prevalence section so the header stays the same
          height across modes. Matches the LSB tabbar header height. */}
      <div style={{ position: "sticky", top: 0, zIndex: 5, background: "white" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "4rem",
            padding: "0 0.4rem 0 1rem",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <TabBarStyle2
              S
              options={visibleModeTabs}
              value={activeMode}
              onChange={(_id, v) => setActiveMode(v)}
              saveValueAsString
            />
          </div>
          {onClose && (
            <ButtonGhostV2 icon="CaretRight" onClick={onClose} />
          )}
        </div>
        <Divider />
      </div>

      {(() => {
        const taxonomyDisplay =
          TAXONOMY_TAB_OPTIONS.find((o) => o.value === activeTaxonomyTab)?.display || "";
        const prevalenceSection = (
          <>
            {/* Taxonomy picker — styled dropdown mirroring the View dropdown
                in cooccurrence. Sticky + border-bottom for parallel structure
                with the cooccurrence section. Dual-agent only. */}
            {showTaxonomyRoleTabs && (
              <div
                style={{
                  position: "sticky",
                  top: subToolbarStickyTop,
                  zIndex: 4,
                  background: "white",
                  borderBottom: `1px solid ${colors2.grey10}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.6rem 1rem",
                }}
              >
                <UI_TAG style={{ color: colors2.grey60 }}>Show:</UI_TAG>
                <DropdownSingle
                  id="prevalenceTaxonomyTab"
                  value={activeTaxonomyTab}
                  onChange={(_k, v) => setActiveTaxonomyTab(v)}
                  options={TAXONOMY_TAB_OPTIONS}
                  saveValueAsString
                  popOutOfOverflowHiddenParent
                  genCustomSelectComp={() => (
                    <StyledToolbarSelectTrigger>
                      <UI_TAG style={{ color: colors2.grey80 }}>
                        {taxonomyDisplay}
                      </UI_TAG>
                      <CaretDown size={10} weight="bold" color={colors2.grey60} />
                    </StyledToolbarSelectTrigger>
                  )}
                />
              </div>
            )}

            {prevalenceResolvedStatus === "loading" && (
              <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
                <SkeletonLoader style={{ width: "100%", height: "160px" }} />
                <SkeletonLoader style={{ width: "100%", height: "160px" }} />
              </div>
            )}

            {prevalenceResolvedStatus === "error" && (
              <div style={{ padding: "1rem" }}>
                <UI_BODY_SM style={{ color: colors2.red60 }}>
                  Could not load chart data.
                  {(annotationError || subThemeCountsError)?.message
                    ? ` ${(annotationError || subThemeCountsError).message}`
                    : ""}
                </UI_BODY_SM>
              </div>
            )}

            {prevalenceResolvedStatus === "success" && (
              <PrevalenceSections
                groups={activeGroups}
                colorIdxMap={colorIdxMap}
                onBarClick={onBarClick}
                barColorByValue={colorCodeColorByTagId}
                renderSectionAction={(group) => (
                  <PaintBucketAction
                    group={group}
                    onToggleBreakdown={toggleSubThemeBreakdown}
                    isBreakdownActive={isSubThemeBreakdownActive}
                  />
                )}
              />
            )}

            {/* Topics sections (split mode) — own gate, see topicsResolvedStatus. */}
            {isSplitMode && !showTaxonomyRoleTabs && topicsResolvedStatus === "loading" && (
              <div style={{ padding: "1rem" }}>
                <SkeletonLoader style={{ width: "100%", height: "160px" }} />
              </div>
            )}

            {isSplitMode && !showTaxonomyRoleTabs && topicsResolvedStatus === "error" && (
              <div style={{ padding: "1rem" }}>
                <UI_BODY_SM style={{ color: colors2.red60 }}>
                  Could not load topics.
                  {topicPrevalenceError?.message ? ` ${topicPrevalenceError.message}` : ""}
                </UI_BODY_SM>
              </div>
            )}

            {isSplitMode && !showTaxonomyRoleTabs && topicsResolvedStatus === "success" && (
              <PrevalenceSections
                groups={topicGroups}
                colorIdxMap={colorIdxMap}
                onBarClick={onBarClick}
                barColorByValue={colorCodeColorByTagId}
                renderSectionAction={(group) => (
                  <PaintBucketAction
                    group={group}
                    onToggleBreakdown={toggleSubThemeBreakdown}
                    isBreakdownActive={isSubThemeBreakdownActive}
                  />
                )}
              />
            )}
          </>
        );

        const cooccurrenceSection = (
          <CooccurrenceSection
            queryActiveFilters={queryActiveFilters}
            enabled={showCooccurrence}
            onRowClick={onCooccurrencePairClick}
            stickyTop={subToolbarStickyTop}
            subThemeDocs={subThemeDocs}
          />
        );

        if (activeMode === "split") {
          // Resizable vertical stack. Approximate panel height via vh —
          // header bar above is ~4rem + small padding, so we reserve 8rem.
          // Each half scrolls independently; the divider between them drags
          // to adjust splitTopPct. Hover/active styling mirrors
          // SidebarLayoutV2's resize-edge convention (1px → 2px on
          // hover/active, colors2.blue accent).
          return (
            <div
              ref={splitContainerRef}
              style={{
                position: "relative",
                height: "calc(100vh - 8rem)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: `${splitTopPct}%`,
                  overflow: "auto",
                }}
              >
                {prevalenceSection}
              </div>
              <SplitResizeHandle
                topPct={splitTopPct}
                isResizing={splitResizing}
                onStart={(e) => {
                  e.preventDefault();
                  setSplitResizing(true);
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: `${splitTopPct}%`,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  overflow: "auto",
                }}
              >
                {cooccurrenceSection}
              </div>
            </div>
          );
        }

        return activeMode === "prevalence"
          ? prevalenceSection
          : cooccurrenceSection;
      })()}
    </div>
  );
};
